import { useCallback, useMemo, useState } from 'react'
import { useProjectMenuStore } from '../../../../../../../zustand/project-menu/use-project-menu-store'
import { useHistory } from 'react-router-dom'
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { tableColumnSizeOptions } from '../../../../../../../components/stories/organisms/table/constants'
import {
  projectTreeItemTypeOptions,
  projectTreeSortByOptions,
  projectTreeSortDirectionOptions,
  projectTreeStateFilterOptions,
  useGetProjectTreeChildrenQueries,
  useGetProjectTreeQuery
} from '../../../../../../../react-query/project/query/use-get-project-tree-query'
import {
  buildColumnsDefault,
  buildColumnsSkeleton,
  buildData,
  buildDataSkeleton
} from '../table-options'
import { flattenRows, getExpandedFolderIds } from '../utils'
import { useCompanyProjectsDnd } from './use-company-projects-dnd'
import { useMeQuery } from '../../../../../../../react-query/authentication/query/use-me-query'
import { serializeQueryString } from '../../../../../../../utils/urlManager'
import { useProjectMenuListViewControl } from '../../../../../../../hooks/use-project-menu-list-view-control'
import { trackFirebaseEvent } from '../../../../../../../firebase/firebase-analytics'

const emptyUserPermissions = []

export const useCompanyProjectsTable = ({ formatMessage }) => {
  const history = useHistory()
  const { data: user } = useMeQuery()
  const userPermissions = useMemo(
    () => user?.permissions ?? emptyUserPermissions,
    [user?.permissions]
  )

  const { viewType, tab } = useProjectMenuListViewControl()

  const [pageNumber, setPageNumber] = useState(0)

  const pageSize = useProjectMenuStore((state) => state.projectListPageSize)
  const setPageSize = useProjectMenuStore(
    (state) => state.setProjectListPageSize
  )

  const search = useProjectMenuStore((state) => state.projectMenuSearch)
  const setSearch = useProjectMenuStore((state) => state.setProjectMenuSearch)
  const showInactive = useProjectMenuStore(
    (state) => state.companyProjectsShowInactive
  )
  const setShowInactive = useProjectMenuStore(
    (state) => state.setCompanyProjectsShowInactive
  )
  const [expanded, setExpanded] = useState({})
  const [rowSelection, setRowSelection] = useState({})
  const [sorting, setSorting] = useState([
    { id: projectTreeSortByOptions.LAST_ACTIVITY, desc: true }
  ])

  const [
    { id: sortBy = projectTreeSortByOptions.LAST_ACTIVITY, desc = true } = {}
  ] = sorting

  const sortDirection = desc
    ? projectTreeSortDirectionOptions.DESC
    : projectTreeSortDirectionOptions.ASC

  const stateFilter = showInactive
    ? projectTreeStateFilterOptions.ALL
    : projectTreeStateFilterOptions.ACTIVE

  const queryParams = useMemo(
    () => ({
      page: pageNumber,
      size: pageSize,
      sortBy,
      sortDirection,
      search,
      stateFilter
    }),
    [pageNumber, pageSize, search, sortBy, sortDirection, stateFilter]
  )

  const { data, isError, isLoading } = useGetProjectTreeQuery({
    params: queryParams
  })

  const expandedFolderIds = useMemo(
    () => getExpandedFolderIds(expanded),
    [expanded]
  )

  const { childrenByFolderId, folderChildrenStateById } =
    useGetProjectTreeChildrenQueries({
      folderIds: expandedFolderIds,
      params: {
        size: 100,
        sortBy,
        sortDirection,
        stateFilter
      }
    })

  const currentPage = data?.pages?.[0]

  const allData = useMemo(() => currentPage?.content ?? [], [currentPage])

  const tableData = useMemo(
    () => buildData(allData, { childrenByFolderId }),
    [allData, childrenByFolderId]
  )

  const rowsById = useMemo(
    () => new Map(flattenRows(tableData).map((row) => [row.id, row])),
    [tableData]
  )

  const selectedItems = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([rowId]) => rowsById.get(rowId))
        .filter(Boolean),
    [rowSelection, rowsById]
  )

  const clearRowSelection = useCallback(() => {
    setRowSelection({})
  }, [])

  const handleOpenProject = useCallback(
    (projectId) => {
      history.push(`/project/${projectId}`)
    },
    [history]
  )

  const handleOpenProjectSettings = useCallback(
    (projectId) => {
      history.push(`/project/settings/${projectId}?view=${viewType}&tab=${tab}`)
    },
    [history]
  )

  const handleMoveItem = useCallback(
    (item) => {
      if (item.type === projectTreeItemTypeOptions.FOLDER) {
        history.push(
          `/project/move/folder/${item.id}?view=${viewType}&tab=${tab}`
        )
        return
      }

      history.push(
        `/project/move/project/${item.id}?view=${viewType}&tab=${tab}`
      )
    },
    [history, viewType, tab]
  )

  const handleCopyProject = useCallback(
    (item) => {
      if (item.type !== projectTreeItemTypeOptions.PROJECT) return

      const queryString = item.parentId
        ? serializeQueryString({ folderId: item.parentId })
        : ''

      history.push(
        `/project/${item.id}/copy${queryString}?view=${viewType}&tab=${tab}`
      )
    },
    [history, viewType, tab]
  )

  const resetPage = useCallback(() => {
    setPageNumber(0)
    setRowSelection({})
  }, [])

  const handlePageChange = useCallback((nextPageNumber) => {
    setPageNumber(Math.max(nextPageNumber, 0))
    setRowSelection({})
  }, [])

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize)
      resetPage()
    },
    [resetPage]
  )

  const handleSearchChange = useCallback(
    (nextSearch) => {
      setSearch(nextSearch)
      resetPage()
    },
    [resetPage]
  )

  const handleShowInactiveChange = useCallback(
    (nextShowInactive) => {
      setShowInactive(nextShowInactive)
      resetPage()
    },
    [resetPage]
  )

  const handleSortingChange = useCallback(
    (updater) => {
      const nextSorting =
        typeof updater === 'function' ? updater(sorting) : updater

      if (nextSorting[0]?.id !== sorting[0]?.id) {
        trackFirebaseEvent('project_list_sort_by_changed', {
          source: 'project_list',
          extra: nextSorting[0]?.id
        })
      }

      if (nextSorting[0]?.desc !== sorting[0]?.desc) {
        trackFirebaseEvent('project_list_sort_direction_changed', {
          source: 'project_list',
          extra: nextSorting[0]?.desc
        })
      }

      setSorting(nextSorting)
      setPageNumber(0)
      setRowSelection({})
    },
    [sorting]
  )

  const handleCreateProjectModal = useCallback(
    (item) => {
      history.push(`/project/new/${item.id}?view=${viewType}&tab=${tab}`)
    },
    [history]
  )

  const handleCreateFolderModal = useCallback(
    (item) => {
      history.push(`/project/newfolder/${item.id}?view=${viewType}&tab=${tab}`)
    },
    [history]
  )

  const onRenameFolder = useCallback(
    (item) => {
      history.push(
        `/project/renameFolder/${item.id}?view=${viewType}&tab=${tab}`
      )
    },
    [history]
  )

  const columnsDefault = useMemo(
    () =>
      buildColumnsDefault({
        formatMessage,
        userPermissions,

        // project handlers
        onCopyProject: handleCopyProject,
        onMoveItem: handleMoveItem,
        onOpenProject: handleOpenProject,
        onOpenProjectSettings: handleOpenProjectSettings,

        // folder handlers
        onCreateProject: handleCreateProjectModal,
        onCreateFolder: handleCreateFolderModal,
        onRenameFolder
      }),
    [
      formatMessage,
      handleCopyProject,
      handleMoveItem,
      handleOpenProject,
      handleOpenProjectSettings,
      userPermissions
    ]
  )

  const columnsSkeleton = useMemo(
    () =>
      buildColumnsSkeleton({
        formatMessage
      }),
    [formatMessage]
  )

  const dnd = useCompanyProjectsDnd({
    formatMessage,
    rowSelection,
    rowsById,
    setExpanded
  })

  const isTableSkeleton = isLoading
  const tableColumns = isTableSkeleton ? columnsSkeleton : columnsDefault

  const tableDataVariant = useMemo(
    () => (isTableSkeleton ? buildDataSkeleton(pageSize) : tableData),
    [isTableSkeleton, pageSize, tableData]
  )

  const table = useReactTable({
    data: tableDataVariant,
    columns: tableColumns,
    state: {
      sorting,
      expanded,
      rowSelection
    },
    defaultColumn: {
      size: 'auto',
      minSize: tableColumnSizeOptions.XXS
    },
    enableRowSelection: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) =>
      !row.original.isSkeleton &&
      row.original.type === projectTreeItemTypeOptions.FOLDER,
    getSubRows: (row) => row.subRows,
    getRowId: (row) => row.id
  })

  const pagination = useMemo(
    () => ({
      pageNumber: currentPage?.number ?? pageNumber,
      pageSize: currentPage?.size ?? pageSize,
      totalElements: currentPage?.totalElements ?? 0,
      totalPages: currentPage?.totalPages ?? 0,
      onPageChange: handlePageChange
    }),
    [currentPage, handlePageChange, pageNumber, pageSize]
  )

  return {
    childrenByFolderId,
    dnd,
    folderChildrenStateById,
    isError,
    pagination,
    pageSize,
    rowSelection,
    rowsById,
    search,
    selectedItems,
    setPageSize: handlePageSizeChange,
    setSearch: handleSearchChange,
    setShowInactive: handleShowInactiveChange,
    showInactive,
    table,
    userPermissions,
    clearRowSelection
  }
}
